/**
 * 🌐 API 엔드포인트 상수 관리
 * 
 * 모든 API 엔드포인트를 중앙에서 관리
 * 서비스별로 그룹화하여 체계적으로 관리
 */

// ===== Base URLs =====
export const API_BASE_URLS = {
  PRODUCTION: 'https://api.ugot.uk',
  STAGING: 'https://staging-api.ugot.uk',
  DEVELOPMENT: 'http://localhost:8080',
  AUTH_SERVER: 'https://auth.ugot.uk',
  API_GATEWAY: 'https://api.ugot.uk',  // API Gateway 통합 엔드포인트
  EDUSENSE: 'https://api.ugot.uk',  // 통합 API 게이트웨이 사용
  USER_SERVER: 'https://api.ugot.uk/api/users',  // User Server (Gateway 경유)
  SOCIAL_SERVER: 'https://api.ugot.uk/api/social',  // Social Server (Gateway 경유)
  REWARD: 'https://reward.ugot.uk',
  PAY: 'https://pay.ugot.uk',
  KAFKA_UI: 'https://kafkaui.ugot.uk',
  MANAGEMENT: 'https://edusense-mgmt.ugot.uk',
  REDIS_INSIGHT: 'https://redis.ugot.uk',
} as const;

// ===== Auth Endpoints =====
export const AUTH_ENDPOINTS = {
  LOGIN: '/api/auth/login',
  LOGOUT: '/api/auth/logout',
  REFRESH: '/api/auth/refresh',
  VERIFY: '/api/auth/verify',
  OAUTH2: {
    REDIRECT: '/oauth2/redirect',
    CALLBACK: '/oauth2/callback',
    AUTHORIZE: '/oauth2/authorize',
    TOKEN: '/oauth2/token',
    REVOKE: '/oauth2/revoke',
  },
  SOCIAL: {
    GOOGLE: '/api/auth/google',
    GITHUB: '/api/auth/github',
    FACEBOOK: '/api/auth/facebook',
  },
} as const;

// ===== User Endpoints =====
export const USER_ENDPOINTS = {
  PROFILE: '/api/users/profile',
  PROFILE_BY_ID: (id: string) => `/api/users/${id}/profile`,
  PROFILE_BY_UUID: (uuid: string) => `/api/users/uuid/${uuid}/profile`,
  UPDATE_PROFILE: '/api/users/profile',
  SETTINGS: '/api/users/settings',
  PREFERENCES: '/api/users/preferences',
  AVATAR: '/api/users/avatar',
  EMAIL: '/api/users/email',
} as const;

// ===== Post/Feed Endpoints =====
export const FEED_ENDPOINTS = {
  LIST: '/api/posts',
  CREATE: '/api/posts',
  GET_BY_ID: (id: string) => `/api/posts/${id}`,
  UPDATE: (id: string) => `/api/posts/${id}`,
  DELETE: (id: string) => `/api/posts/${id}`,
  LIKE: (id: string) => `/api/posts/${id}/like`,
  UNLIKE: (id: string) => `/api/posts/${id}/unlike`,
  COMMENT: (id: string) => `/api/posts/${id}/comments`,
  SHARE: (id: string) => `/api/posts/${id}/share`,
} as const;

// ===== Course/Learning Endpoints =====
export const COURSE_ENDPOINTS = {
  LIST: '/api/courses',
  GET_BY_ID: (id: string) => `/api/courses/${id}`,
  ENROLL: (id: string) => `/api/courses/${id}/enroll`,
  PROGRESS: (id: string) => `/api/courses/${id}/progress`,
  COMPLETE: (id: string) => `/api/courses/${id}/complete`,
  LESSONS: (courseId: string) => `/api/courses/${courseId}/lessons`,
  LESSON_BY_ID: (courseId: string, lessonId: string) => 
    `/api/courses/${courseId}/lessons/${lessonId}`,
} as const;

// ===== Project Endpoints =====
export const PROJECT_ENDPOINTS = {
  LIST: '/api/projects',
  CREATE: '/api/projects',
  GET_BY_ID: (id: string) => `/api/projects/${id}`,
  UPDATE: (id: string) => `/api/projects/${id}`,
  DELETE: (id: string) => `/api/projects/${id}`,
  UPLOAD: '/api/projects/upload',
  FORK: (id: string) => `/api/projects/${id}/fork`,
  STAR: (id: string) => `/api/projects/${id}/star`,
} as const;

// ===== Notification Endpoints =====
export const NOTIFICATION_ENDPOINTS = {
  LIST: '/api/notifications',
  DETAIL: (id: string | number) => `/api/notifications/${id}`,
  READ_ALL: '/api/notifications/read/all',
  SSE_SUBSCRIBE: (userId: string | number) => `/api/notifications/sse/subscribe/${userId}`,
} as const;

// ===== Chat/Message Endpoints =====
export const CHAT_ENDPOINTS = {
  CONVERSATIONS: '/api/chat/conversations',
  MESSAGES: (conversationId: string) => `/api/chat/conversations/${conversationId}/messages`,
  SEND: (conversationId: string) => `/api/chat/conversations/${conversationId}/send`,
  TYPING: (conversationId: string) => `/api/chat/conversations/${conversationId}/typing`,
  READ: (conversationId: string) => `/api/chat/conversations/${conversationId}/read`,
} as const;

// ===== Admin Endpoints =====
export const ADMIN_ENDPOINTS = {
  USERS: '/api/admin/users',
  USER_BY_ID: (id: string) => `/api/admin/users/${id}`,
  USER_BY_USERNAME: (username: string) => `/api/admin/users/${username}`,
  TEST_USERS: '/api/admin/test-users',
  TEST_USERS_BATCH: '/api/admin/test-users/batch',
  POSTS: '/api/admin/posts',
  ANALYTICS: '/api/admin/analytics',
  REPORTS: '/api/admin/reports',
  SETTINGS: '/api/admin/settings',
} as const;

// ===== Health/Status Endpoints =====
export const HEALTH_ENDPOINTS = {
  HEALTH: '/health',
  READY: '/ready',
  LIVE: '/live',
  METRICS: '/metrics',
  STATUS: '/api/status',
} as const;

// ===== WebSocket Endpoints =====
export const WEBSOCKET_ENDPOINTS = {
  CHAT: '/ws/chat',
  NOTIFICATIONS: '/ws/notifications',
  PRESENCE: '/ws/presence',
  COLLABORATION: '/ws/collaboration',
} as const;

// ===== Proxy Endpoints (for OAuth) =====
export const PROXY_ENDPOINTS = {
  TOKEN: '/api/proxy/token',
  USERINFO: '/api/proxy/userinfo',
  INTROSPECT: '/api/proxy/introspect',
} as const;

// ===== Debug / Diagnostics Endpoints (AuthServer) =====
export const DEBUG_ENDPOINTS = {
  HEADERS: '/_debug/headers',
  CREATE_TEST_USER: '/_debug/create-test-user',
  TRIGGER_USER_EVENT: '/_debug/trigger-user-event',
  OAUTH2_CLEAR_AUTHORIZATIONS: '/_debug/oauth2/authorizations',
  OAUTH2_CLEAR_ALL: '/_debug/oauth2/all',
  OAUTH2_AUTH_COUNT: '/_debug/oauth2/authorizations/count',
  OAUTH2_CLIENTS: '/_debug/oauth2/clients',
  ADMIN_POST_TEST: '/_debug/admin-post-test',
  ADMIN_GET_TEST: '/_debug/admin-get-test',
} as const;

// Export all endpoints in one object for convenience
export const API_ENDPOINTS = {
  auth: AUTH_ENDPOINTS,
  user: USER_ENDPOINTS,
  feed: FEED_ENDPOINTS,
  course: COURSE_ENDPOINTS,
  project: PROJECT_ENDPOINTS,
  notification: NOTIFICATION_ENDPOINTS,
  chat: CHAT_ENDPOINTS,
  admin: ADMIN_ENDPOINTS,
  health: HEALTH_ENDPOINTS,
  websocket: WEBSOCKET_ENDPOINTS,
  proxy: PROXY_ENDPOINTS,
  debug: DEBUG_ENDPOINTS,
} as const;

// Type exports for type safety
export type ApiBaseUrl = typeof API_BASE_URLS[keyof typeof API_BASE_URLS];
export type AuthEndpoint = typeof AUTH_ENDPOINTS[keyof typeof AUTH_ENDPOINTS];
export type UserEndpoint = typeof USER_ENDPOINTS[keyof typeof USER_ENDPOINTS];
export type FeedEndpoint = typeof FEED_ENDPOINTS[keyof typeof FEED_ENDPOINTS];
export type CourseEndpoint = typeof COURSE_ENDPOINTS[keyof typeof COURSE_ENDPOINTS];
export type ProjectEndpoint = typeof PROJECT_ENDPOINTS[keyof typeof PROJECT_ENDPOINTS];
export type NotificationEndpoint = typeof NOTIFICATION_ENDPOINTS[keyof typeof NOTIFICATION_ENDPOINTS];
export type DebugEndpoint = typeof DEBUG_ENDPOINTS[keyof typeof DEBUG_ENDPOINTS];
